import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Search } from "lucide-react";
import Input from "@/components/ui/Input";
import Button from "@/components/ui/Button";

const LookupSchema = z.object({
  email: z.string().email("Enter a valid email address"),
  bookingRef: z.string().min(1, "Enter your booking reference"),
});

type LookupFormData = z.infer<typeof LookupSchema>;

interface BookingLookupFormProps {
  onSubmit: (email: string, bookingRef: string) => void;
  loading: boolean;
  error: string | null;
}

export default function BookingLookupForm({
  onSubmit,
  loading,
  error,
}: BookingLookupFormProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LookupFormData>({
    resolver: zodResolver(LookupSchema),
  });

  return (
    <form
      onSubmit={handleSubmit((d) => onSubmit(d.email, d.bookingRef))}
      noValidate
      className="space-y-4"
    >
      <Input
        label="Email Address"
        type="email"
        required
        placeholder="rahul@example.com"
        hint="The email address used when booking."
        error={errors.email?.message}
        {...register("email")}
      />
      <Input
        label="Booking Reference"
        required
        placeholder="HTL-20260421-XXXX"
        hint="Found in your booking confirmation."
        error={errors.bookingRef?.message}
        {...register("bookingRef")}
      />

      {error && (
        <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-600">
          {error}
        </div>
      )}

      <Button
        type="submit"
        fullWidth
        leftIcon={<Search size={16} />}
        disabled={loading}
      >
        {loading ? "Looking up…" : "Find My Booking"}
      </Button>
    </form>
  );
}